import React, { createContext, useContext, useEffect, useState } from 'react';

export type ThemeType = 'original' | 'forest' | 'sunset' | 'ocean' | 'lavender';

interface ThemeContextType {
  theme: ThemeType;
  setTheme: (theme: ThemeType) => void;
}

const THEME_CLASSES: Record<ThemeType, string> = {
  original: 'theme-original',
  forest: 'theme-forest',
  sunset: 'theme-sunset',
  ocean: 'theme-ocean',
  lavender: 'theme-lavender',
};

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const ThemeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [theme, setThemeState] = useState<ThemeType>(() => {
    if (typeof window === 'undefined') {
      return 'original';
    }

    const savedTheme = window.localStorage.getItem('sandra-theme');
    return (savedTheme as ThemeType) || 'original';
  });

  const setTheme = (newTheme: ThemeType) => {
    setThemeState(newTheme);
    window.localStorage.setItem('sandra-theme', newTheme);
  };

  useEffect(() => {
    const root = document.documentElement;
    Object.values(THEME_CLASSES).forEach(themeClass => root.classList.remove(themeClass));
    root.classList.add(THEME_CLASSES[theme]);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }

  return context;
};
